import { Toast as RadixToast } from 'radix-ui'
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import { cn } from '../cn'

export type ToastTone = 'success' | 'error' | 'info'

type ToastMessage = { id: number; title: string; description?: string; tone: ToastTone }

const ToastContext = createContext<((message: Omit<ToastMessage, 'id'>) => void) | null>(null)

/** Bildirim göstermek için: `const toast = useToast(); toast({ title: '...' })`. */
export function useToast() {
  const context = useContext(ToastContext)
  if (!context) throw new Error('useToast, ToastProvider içinde çağrılmalı')
  return context
}

const toneClass: Record<ToastTone, string> = {
  success: 'border-l-[var(--ad-success)]',
  error: 'border-l-[var(--ad-danger)]',
  info: 'border-l-[var(--ad-primary)]',
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [messages, setMessages] = useState<ToastMessage[]>([])

  const push = useCallback((message: Omit<ToastMessage, 'id'>) => {
    setMessages((current) => [...current, { ...message, id: Date.now() + Math.random() }])
  }, [])

  const value = useMemo(() => push, [push])

  return (
    <ToastContext.Provider value={value}>
      <RadixToast.Provider swipeDirection="right">
        {children}
        {messages.map((message) => (
          <RadixToast.Root
            key={message.id}
            duration={5000}
            onOpenChange={(open) =>
              !open && setMessages((current) => current.filter((item) => item.id !== message.id))
            }
            className={cn(
              'rounded-[var(--radius-control)] border border-l-4 border-[var(--ad-border)] bg-[var(--ad-surface)] px-4 py-3 shadow-lg',
              toneClass[message.tone],
            )}
          >
            <RadixToast.Title className="text-sm font-medium">{message.title}</RadixToast.Title>
            {message.description && (
              <RadixToast.Description className="mt-0.5 text-xs text-[var(--ad-text-muted)]">
                {message.description}
              </RadixToast.Description>
            )}
          </RadixToast.Root>
        ))}
        <RadixToast.Viewport className="fixed right-4 bottom-4 z-50 flex w-80 flex-col gap-2" />
      </RadixToast.Provider>
    </ToastContext.Provider>
  )
}
